import * as React from "react"

import { USE_MOCKS, api } from "@/lib/api"
import { setActiveLocale, type LocaleSpec } from "@/lib/format"
import { DICCIONARIOS } from "@/lib/locales"

/**
 * DOS EJES, Y HASTA HOY ESTABAN PEGADOS EN UNO.
 *
 * Cesar, punto 12: «traducción para que cada usuario pueda seleccionar idioma»,
 * y despues lo preciso: «el idioma deberia de poder seleccionarlo cada usuario,
 * no por entidad».
 *
 * 1. **EL IDIOMA DE LA INTERFAZ** («Guardar» / «Save» / «Salvar») es de la
 *    PERSONA. Un contador brasileño que lleva la sociedad peruana quiere el
 *    producto en portugues, y no por eso deja de mirar una empresa de Lima. Se
 *    guarda en su perfil (`app_user.idioma`), no en el navegador: quien entra
 *    desde otra maquina no tiene que volver a elegir.
 *
 * 2. **LA TERMINOLOGIA FISCAL, LA MONEDA Y EL FORMATO** son de la SOCIEDAD, y
 *    eso ya estaba bien. El ICMS es ICMS y el RUC es RUC sin importar quien
 *    mire: son nombres legales, aparecen tal cual en el documento y en el
 *    formulario, y traducir «CNPJ» a «RUC» seria inventar un dato. Lo mismo el
 *    formato: una cifra de una empresa de Sao Paulo se escribe como la escribe
 *    Sao Paulo, la lea quien la lea.
 *
 * Pegados en un eje, cambiar de sociedad cambiaba el idioma de los botones, y
 * elegir idioma habria cambiado el nombre de los impuestos.
 *
 * **SIN IDIOMA ELEGIDO SE SIGUE AL PAIS DE LA SOCIEDAD**, que es lo que hacia
 * hasta hoy y es un buen valor por defecto. `null` significa «no he elegido», no
 * «español»: si `null` se tratara como español, un contador de Sao Paulo veria
 * el producto en español sin haberlo pedido nunca.
 *
 * El catalogo local espeja `backend/app/core/i18n.py` para que la SPA funcione
 * con mocks y para que un fallo de red no deje la pantalla sin unidades.
 */

export interface I18nFormat {
  simbolo: string
  sep_miles: string
  sep_decimal: string
  fecha: string
  simbolo_antes: boolean
}

export interface Terminology {
  impuesto_ventas: string
  identificador_fiscal: string
  autoridad: string
  comprobante: string
  libro_compras: string
  libro_ventas: string
  declaracion_mensual: string
  plan_de_cuentas: string
}

export interface I18nPackage {
  locale: string
  idioma: "es" | "pt" | "en"
  pais: string
  moneda: string
  formato: I18nFormat
  terminologia: Terminology
  mensajes: Record<string, string>
}

/* ------------------------------------------------------------------ */
/* Catalogo local — espejo del rulepack del backend                    */
/* ------------------------------------------------------------------ */

interface LocaleRow {
  locale: string
  idioma: "es" | "pt" | "en"
  moneda: string
  simbolo: string
  sepMiles: string
  sepDecimal: string
  fecha: string
  simboloAntes: boolean
}

const LOCALES: Record<string, LocaleRow> = {
  PE: { locale: "es-PE", idioma: "es", moneda: "PEN", simbolo: "S/", sepMiles: ",", sepDecimal: ".", fecha: "dd/mm/aaaa", simboloAntes: true },
  CL: { locale: "es-CL", idioma: "es", moneda: "CLP", simbolo: "$", sepMiles: ".", sepDecimal: ",", fecha: "dd-mm-aaaa", simboloAntes: true },
  CO: { locale: "es-CO", idioma: "es", moneda: "COP", simbolo: "$", sepMiles: ".", sepDecimal: ",", fecha: "dd/mm/aaaa", simboloAntes: true },
  MX: { locale: "es-MX", idioma: "es", moneda: "MXN", simbolo: "$", sepMiles: ",", sepDecimal: ".", fecha: "dd/mm/aaaa", simboloAntes: true },
  PA: { locale: "es-PA", idioma: "es", moneda: "PAB", simbolo: "B/.", sepMiles: ",", sepDecimal: ".", fecha: "dd/mm/aaaa", simboloAntes: true },
  AR: { locale: "es-AR", idioma: "es", moneda: "ARS", simbolo: "$", sepMiles: ".", sepDecimal: ",", fecha: "dd/mm/aaaa", simboloAntes: true },
  ES: { locale: "es-ES", idioma: "es", moneda: "EUR", simbolo: "€", sepMiles: ".", sepDecimal: ",", fecha: "dd/mm/aaaa", simboloAntes: false },
  BR: { locale: "pt-BR", idioma: "pt", moneda: "BRL", simbolo: "R$", sepMiles: ".", sepDecimal: ",", fecha: "dd/mm/aaaa", simboloAntes: true },
  PT: { locale: "pt-PT", idioma: "pt", moneda: "EUR", simbolo: "€", sepMiles: " ", sepDecimal: ",", fecha: "dd/mm/aaaa", simboloAntes: false },
  US: { locale: "en-US", idioma: "en", moneda: "USD", simbolo: "$", sepMiles: ",", sepDecimal: ".", fecha: "mm/dd/yyyy", simboloAntes: true },
}

/** Nombres legales. Se muestran tal cual, sin traducir jamas. */
const TERMINOLOGIA: Record<string, Terminology> = {
  PE: { impuesto_ventas: "IGV", identificador_fiscal: "RUC", autoridad: "SUNAT", comprobante: "comprobante de pago", libro_compras: "Registro de Compras", libro_ventas: "Registro de Ventas e Ingresos", declaracion_mensual: "Formulario Virtual 621", plan_de_cuentas: "PCGE" },
  CL: { impuesto_ventas: "IVA", identificador_fiscal: "RUT", autoridad: "SII", comprobante: "documento tributario electrónico", libro_compras: "Libro de Compras", libro_ventas: "Libro de Ventas", declaracion_mensual: "Formulario 29", plan_de_cuentas: "plan de cuentas" },
  CO: { impuesto_ventas: "IVA", identificador_fiscal: "NIT", autoridad: "DIAN", comprobante: "factura electrónica", libro_compras: "Libro de Compras", libro_ventas: "Libro de Ventas", declaracion_mensual: "Formulario 300", plan_de_cuentas: "PUC" },
  MX: { impuesto_ventas: "IVA", identificador_fiscal: "RFC", autoridad: "SAT", comprobante: "CFDI", libro_compras: "Registro de Compras", libro_ventas: "Registro de Ventas", declaracion_mensual: "Declaración mensual", plan_de_cuentas: "Código Agrupador SAT" },
  PA: { impuesto_ventas: "ITBMS", identificador_fiscal: "RUC", autoridad: "DGI", comprobante: "factura", libro_compras: "Registro de Compras", libro_ventas: "Registro de Ventas", declaracion_mensual: "Formulario 430", plan_de_cuentas: "plan de cuentas" },
  AR: { impuesto_ventas: "IVA", identificador_fiscal: "CUIT", autoridad: "ARCA", comprobante: "comprobante electrónico", libro_compras: "Libro IVA Compras", libro_ventas: "Libro IVA Ventas", declaracion_mensual: "Formulario 2002", plan_de_cuentas: "plan de cuentas" },
  ES: { impuesto_ventas: "IVA", identificador_fiscal: "NIF", autoridad: "AEAT", comprobante: "factura", libro_compras: "Libro registro de facturas recibidas", libro_ventas: "Libro registro de facturas expedidas", declaracion_mensual: "Modelo 303", plan_de_cuentas: "Plan General Contable" },
  BR: { impuesto_ventas: "ICMS/ISS", identificador_fiscal: "CNPJ", autoridad: "Receita Federal", comprobante: "nota fiscal", libro_compras: "Registro de Entradas", libro_ventas: "Registro de Saídas", declaracion_mensual: "SPED / EFD", plan_de_cuentas: "plano de contas" },
  PT: { impuesto_ventas: "IVA", identificador_fiscal: "NIF", autoridad: "AT", comprobante: "fatura", libro_compras: "Registo de compras", libro_ventas: "Registo de vendas", declaracion_mensual: "Declaração periódica de IVA", plan_de_cuentas: "SNC" },
  US: { impuesto_ventas: "Sales Tax", identificador_fiscal: "EIN", autoridad: "IRS", comprobante: "invoice", libro_compras: "Purchase Journal", libro_ventas: "Sales Journal", declaracion_mensual: "Sales tax return", plan_de_cuentas: "chart of accounts" },
}

/**
 * Las cadenas de interfaz, en los tres idiomas.
 *
 * ---------------------------------------------------------------------------
 * QUÉ ENTRA AQUÍ Y QUÉ NO
 *
 * Entra lo que es **crónica del producto**: el riel, los rótulos del alcance,
 * los verbos de los botones, los nombres de los libros. Es lo que se ve en
 * todas las pantallas, así que es lo que hace que elegir idioma se note.
 *
 * NO entra la terminología fiscal —IGV, RUC, SUNAT, CNPJ—, que va en
 * `TERMINOLOGIA` y sigue a la SOCIEDAD: son nombres legales y traducirlos
 * inventaría un dato.
 *
 * Y NO entra —todavía— el texto largo de cada pantalla. Quedan ~1.400 cadenas
 * escritas en español dentro de 69 archivos de rutas; extraerlas es mecánico y
 * grande, y está anotado en el traspaso. Lo que SÍ está entero en tres idiomas
 * desde antes es **la voz de Marty** (`lib/marty.ts`): las aperturas de las
 * veinticuatro secciones y los estados de carga, vacío y error de cada una. Eso
 * hace que cambiar de idioma cambie de verdad lo que el producto DICE, no sólo
 * cómo se llaman sus menús.
 */
const MENSAJES: Record<"es" | "pt" | "en", Record<string, string>> = {
  es: {
    nav_primeros_pasos: "Primeros pasos",
    nav_primeros_pasos_hint: "Traer tu histórico desde otra plataforma",
    nav_dashboard: "Dashboard",
    nav_dashboard_hint: "Métricas de finanzas y contabilidad",
    nav_finanzas: "Finanzas",
    nav_finanzas_hint: "P&L, caja, revenue, gastos, payroll",
    nav_contabilidad: "Contabilidad",
    nav_contabilidad_hint: "Revisión, libros, maestros, declaraciones",
    nav_holding: "Holding",
    nav_holding_hint: "Consolidado, intercompañía, tesorería",
    nav_auditoria: "Auditoría",
    nav_auditoria_hint: "Quién cambió qué, cuándo y por qué",
    nav_pendientes: "Pendientes",
    nav_pendientes_hint: "Lo que alguien marcó que no cuadra",
    nav_accesos: "Accesos",
    nav_accesos_hint: "Quién entra y con qué alcance",
    cliente: "Cliente",
    sociedad: "Sociedad",
    descargar: "Descargar",
    cancelar: "Cancelar",
    guardar: "Guardar",
    reintentar: "Reintentar",
    cerrar_sesion: "Cerrar sesión",
    solo_lectura: "Solo lectura",
    libro_diario: "Libro diario",
    libro_mayor: "Libro mayor",
    balance_comprobacion: "Balance de comprobación",
    analisis_cuenta: "Análisis de cuenta",
    cola_revision: "Cola de revisión",
    motivo_requerido: "El motivo es obligatorio",
    periodo_cerrado: "El período está cerrado. Registra un asiento de reversión.",
    no_cuadra: "El balance no cuadra",
    cuadra: "El balance cuadra",
    requiere_revision: "Requiere revisión",
    confianza: "Confianza",
    fundamento: "Fundamento",
    costo_de_ventas: "Costo de ventas",
    gasto_operativo: "Gasto operativo",
    margen_bruto: "Margen bruto",
    flujo_de_caja: "Flujo de caja",
    firmar: "Firmar",
    separacion_funciones: "Quien preparó esta declaración no puede firmarla.",
    debe: "Debe",
    haber: "Haber",
    saldo: "Saldo",
    cuenta: "Cuenta",
    periodo: "Período",
    diferencia: "Diferencia",
  },
  pt: {
    nav_primeros_pasos: "Primeiros passos",
    nav_primeros_pasos_hint: "Trazer seu histórico de outra plataforma",
    nav_dashboard: "Painel",
    nav_dashboard_hint: "Métricas de finanças e contabilidade",
    nav_finanzas: "Finanças",
    nav_finanzas_hint: "DRE, caixa, receita, despesas, folha",
    nav_contabilidad: "Contabilidade",
    nav_contabilidad_hint: "Revisão, livros, cadastros, obrigações",
    nav_holding: "Holding",
    nav_holding_hint: "Consolidado, intercompanhia, tesouraria",
    nav_auditoria: "Auditoria",
    nav_auditoria_hint: "Quem mudou o quê, quando e por quê",
    nav_pendientes: "Pendências",
    nav_pendientes_hint: "O que alguém marcou que não bate",
    nav_accesos: "Acessos",
    nav_accesos_hint: "Quem entra e com que alcance",
    cliente: "Cliente",
    sociedad: "Empresa",
    descargar: "Baixar",
    cancelar: "Cancelar",
    guardar: "Salvar",
    reintentar: "Tentar de novo",
    cerrar_sesion: "Sair",
    solo_lectura: "Somente leitura",
    libro_diario: "Livro diário",
    libro_mayor: "Livro razão",
    balance_comprobacion: "Balancete de verificação",
    analisis_cuenta: "Análise de conta",
    cola_revision: "Fila de revisão",
    motivo_requerido: "O motivo é obrigatório",
    periodo_cerrado: "O período está fechado. Registre um lançamento de estorno.",
    no_cuadra: "O balancete não fecha",
    cuadra: "O balancete fecha",
    requiere_revision: "Requer revisão",
    confianza: "Confiança",
    fundamento: "Fundamentação",
    costo_de_ventas: "Custo dos serviços prestados",
    gasto_operativo: "Despesa operacional",
    margen_bruto: "Margem bruta",
    flujo_de_caja: "Fluxo de caixa",
    firmar: "Assinar",
    separacion_funciones: "Quem preparou esta declaração não pode assiná-la.",
    debe: "Débito",
    haber: "Crédito",
    saldo: "Saldo",
    cuenta: "Conta",
    periodo: "Período",
    diferencia: "Diferença",
  },
  en: {
    nav_primeros_pasos: "First steps",
    nav_primeros_pasos_hint: "Bring your history over from another platform",
    nav_dashboard: "Dashboard",
    nav_dashboard_hint: "Finance and accounting metrics",
    nav_finanzas: "Finance",
    nav_finanzas_hint: "P&L, cash, revenue, expenses, payroll",
    nav_contabilidad: "Accounting",
    nav_contabilidad_hint: "Review, books, master data, filings",
    nav_holding: "Holding",
    nav_holding_hint: "Consolidation, intercompany, treasury",
    nav_auditoria: "Audit trail",
    nav_auditoria_hint: "Who changed what, when and why",
    nav_pendientes: "Open items",
    nav_pendientes_hint: "What someone flagged as off",
    nav_accesos: "Access",
    nav_accesos_hint: "Who gets in, and how far",
    cliente: "Client",
    sociedad: "Company",
    descargar: "Download",
    cancelar: "Cancel",
    guardar: "Save",
    reintentar: "Try again",
    cerrar_sesion: "Sign out",
    solo_lectura: "Read only",
    libro_diario: "General journal",
    libro_mayor: "General ledger",
    balance_comprobacion: "Trial balance",
    analisis_cuenta: "Account analysis",
    cola_revision: "Review queue",
    motivo_requerido: "A reason is required",
    periodo_cerrado: "The period is closed. Post a reversing entry instead.",
    no_cuadra: "Trial balance does not balance",
    cuadra: "Trial balance is in balance",
    requiere_revision: "Needs review",
    confianza: "Confidence",
    fundamento: "Rationale",
    costo_de_ventas: "Cost of sales",
    gasto_operativo: "Operating expense",
    margen_bruto: "Gross margin",
    flujo_de_caja: "Cash flow",
    firmar: "Sign",
    separacion_funciones: "Whoever prepared this return cannot sign it.",
    debe: "Debit",
    haber: "Credit",
    saldo: "Balance",
    cuenta: "Account",
    periodo: "Period",
    diferencia: "Difference",
  },
}

export const SUPPORTED_COUNTRIES = Object.keys(LOCALES)

export function localPackage(country: string | null | undefined): I18nPackage {
  const code = (country ?? "PE").toUpperCase()
  const row = LOCALES[code] ?? LOCALES.PE
  const pais = LOCALES[code] ? code : "PE"
  return {
    locale: row.locale,
    idioma: row.idioma,
    pais,
    moneda: row.moneda,
    formato: {
      simbolo: row.simbolo,
      sep_miles: row.sepMiles,
      sep_decimal: row.sepDecimal,
      fecha: row.fecha,
      simbolo_antes: row.simboloAntes,
    },
    terminologia: TERMINOLOGIA[pais] ?? TERMINOLOGIA.PE,
    mensajes: MENSAJES[row.idioma],
  }
}

/* ==================================================================== */
/* LA TRADUCCIÓN, FUERA DE REACT                                        */
/* ==================================================================== */

/**
 * El idioma activo, como variable de módulo.
 *
 * **POR QUÉ NO ES SOLO UN HOOK.** Las 1.398 cadenas del producto no están todas
 * dentro de componentes: hay catálogos de datos —`TEXTOS` en
 * `routes/onboarding/pasos.tsx`, las etiquetas de los estados, los mapas de
 * columnas— que son objetos de módulo, y ahí no se puede llamar a un hook. Con
 * `t()` como función suelta, la misma llamada sirve en los tres sitios y la
 * migración fue un reemplazo mecánico en vez de una reescritura.
 *
 * Es el mismo patrón que `setActiveLocale` en `lib/format.ts`, que ya resolvía
 * lo mismo para el formato numérico.
 */
let idiomaActivo: "es" | "pt" | "en" = "es"

/** Lo llama `I18nProvider`. No se usa desde ningún otro sitio. */
export function setIdiomaActivo(idioma: "es" | "pt" | "en"): void {
  idiomaActivo = idioma
}

/**
 * Traduce. **La clave es la frase en español.**
 *
 * Si no hay traducción, devuelve la clave — que es el español. Ver el docstring
 * de `lib/locales/index.ts`: esa degradación es lo que hace segura una
 * migración de 1.398 cadenas, porque lo peor que puede pasar es que una frase
 * se quede en español.
 */
export function t(frase: string): string {
  return DICCIONARIOS[idiomaActivo]?.[frase] ?? frase
}

/* ------------------------------------------------------------------ */
/* Contexto                                                            */
/* ------------------------------------------------------------------ */

interface I18nContextValue extends I18nPackage {
  /** Cadena de interfaz en el idioma del USUARIO. Ver el docstring. */
  t: (key: string) => string
  /** Nombre legal (IGV, RUC, SUNAT...). Nunca se traduce. */
  term: Terminology
  spec: LocaleSpec
  /** true mientras se consulta GET /i18n/{pais}; el catalogo local ya sirve. */
  loading: boolean
}

const I18nContext = React.createContext<I18nContextValue | null>(null)

export function I18nProvider({
  country,
  idiomaDelUsuario,
  children,
}: {
  country: string | null | undefined
  /**
   * El idioma que eligio ESTA persona, o `null` si no eligio ninguno.
   *
   * `null` no es «español»: es «sigue al pais de la sociedad». Ver el docstring
   * del modulo.
   */
  idiomaDelUsuario?: "es" | "pt" | "en" | null
  children: React.ReactNode
}) {
  const code = (country ?? "PE").toUpperCase()
  const [pkg, setPkg] = React.useState<I18nPackage>(() => localPackage(code))
  const [loading, setLoading] = React.useState(false)

  React.useEffect(() => {
    const local = localPackage(code)
    setPkg(local)
    /*
      EL IDIOMA DEL TEXTO, que es de la PERSONA y no del país. Se resuelve aquí
      —y no sólo abajo, donde se arma el contexto— porque `setActiveLocale`
      alimenta la `t` y los formatos de MÓDULO, que se usan fuera de React: sin
      esto, el nombre del mes seguiría saliendo en el idioma del país.
      Ver `LocaleSpec.localeTexto` en `lib/format.ts`.
    */
    const idiomaTexto = idiomaDelUsuario ?? local.idioma
    // El locale se aplica antes de pedir nada: la pantalla nunca se pinta con
    // el formato del pais anterior mientras viaja la respuesta.
    setActiveLocale({ locale: local.locale, localeTexto: idiomaTexto, currency: local.moneda })

    if (USE_MOCKS) return
    let cancelled = false
    setLoading(true)
    api
      .get<I18nPackage>(`/i18n/${code}`)
      .then((remote) => {
        if (cancelled || !remote?.locale) return
        setPkg(remote)
        setActiveLocale({ locale: remote.locale, localeTexto: idiomaTexto, currency: remote.moneda })
      })
      .catch(() => {
        /* El catalogo local ya esta aplicado: un 500 no deja la UI sin unidades. */
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // `idiomaDelUsuario` ES DEPENDENCIA, y faltaba. El efecto sólo miraba el
    // país, así que al cambiar de idioma el locale del TEXTO se quedaba en el
    // anterior: la interfaz pasaba a español y los meses seguían en portugués.
    // El mismo defecto que esto venía a arreglar, una capa más abajo.
  }, [code, idiomaDelUsuario])

  const value = React.useMemo<I18nContextValue>(() => {
    /*
      AQUI SE SEPARAN LOS DOS EJES, y es todo el cambio.

      `idioma` manda en las CADENAS DE INTERFAZ. `locale`, `moneda`, `formato` y
      `terminologia` siguen siendo los de la sociedad, intactos: el idioma de
      quien mira no puede cambiar cuanto vale un peso ni como se llama un
      impuesto.

      Y el `locale` de formato NO se toca tampoco. Es lo que decide si mil
      doscientos treinta y cuatro con cincuenta se escribe «1.234,50» o
      «1,234.50», y eso es una propiedad del PAIS de la cifra, no del lector: un
      balance brasileño leido por un peruano se sigue presentando a la
      brasileña, porque es el que se va a firmar en Brasil.
    */
    const idioma = idiomaDelUsuario ?? pkg.idioma
    const mensajes = MENSAJES[idioma] ?? pkg.mensajes
    // La `t` de módulo lee esta variable. Se fija durante el render y no en un
    // efecto porque los catálogos de datos se evalúan al importarse, y un
    // efecto llegaría tarde para el primer pintado.
    setIdiomaActivo(idioma)
    return {
      ...pkg,
      idioma,
      mensajes,
      term: pkg.terminologia,
      spec: { locale: pkg.locale, currency: pkg.moneda },
      loading,
      /*
        `t` del contexto y `t` de módulo son LA MISMA FUNCIÓN por dentro. Se
        deja en el contexto porque diecisiete componentes ya la consumían así, y
        tener dos implementaciones sería tener dos catálogos que algún día
        divergen.

        Atiende primero las claves cortas heredadas (`libro_diario`,
        `nav_finanzas`…) y después el diccionario de frases. Las dos formas
        conviven a propósito: las claves cortas son para lo que cambia de
        PALABRA según el país, como el nombre de un libro.
      */
      t: (clave: string) => mensajes?.[clave] ?? MENSAJES.es[clave] ?? t(clave),
    }
  }, [pkg, loading, idiomaDelUsuario])

  /*
    LA `key` REMONTA EL ÁRBOL AL CAMBIAR DE IDIOMA, y es deliberado.

    `t()` es una función de módulo, no un hook, así que los componentes que la
    usan NO están suscritos al contexto: cambiar el idioma no los volvería a
    pintar por sí solo y la pantalla se quedaría a medio traducir hasta navegar
    a otro sitio.

    Remontar es brusco —se pierden los filtros locales de la pantalla— y es el
    precio correcto: cambiar de idioma es una acción explícita y rara, y lo que
    no se puede permitir es media pantalla en cada idioma.
  */
  return (
    <I18nContext.Provider value={value}>
      <React.Fragment key={value.idioma}>{children}</React.Fragment>
    </I18nContext.Provider>
  )
}

export function useI18n(): I18nContextValue {
  const ctx = React.useContext(I18nContext)
  if (ctx) return ctx
  // Fuera del provider (por ejemplo en el login) se responde con Peru: el
  // sistema siempre tiene unidades, nunca strings crudos.
  const pkg = localPackage("PE")
  return {
    ...pkg,
    term: pkg.terminologia,
    spec: { locale: pkg.locale, currency: pkg.moneda },
    loading: false,
    t: (clave: string) => pkg.mensajes[clave] ?? t(clave),
  }
}
